import { useEffect, useId, useRef, useState } from "react";
import type { EChartsOption } from "echarts";
import * as echarts from "echarts/core";
import {
  BarChart,
  GraphChart,
  HeatmapChart,
  LineChart,
  SankeyChart,
  ScatterChart,
} from "echarts/charts";
import {
  CalendarComponent,
  DatasetComponent,
  DataZoomComponent,
  GridComponent,
  LegendComponent,
  MarkAreaComponent,
  MarkLineComponent,
  TooltipComponent,
  VisualMapComponent,
} from "echarts/components";
import { CanvasRenderer } from "echarts/renderers";

echarts.use([
  BarChart,
  GraphChart,
  HeatmapChart,
  LineChart,
  SankeyChart,
  ScatterChart,
  CalendarComponent,
  DatasetComponent,
  DataZoomComponent,
  GridComponent,
  LegendComponent,
  MarkAreaComponent,
  MarkLineComponent,
  TooltipComponent,
  VisualMapComponent,
  CanvasRenderer,
]);

export const palette = {
  ink: "#1c211f",
  muted: "#6c7470",
  line: "#deded8",
  green: "#176b5b",
  greenSoft: "#73a093",
  amber: "#a86413",
  red: "#a83e33",
  blue: "#326887",
  paper: "#fbfbf8",
} as const;
export const chartText = {
  fontFamily: "DM Sans, sans-serif",
  color: palette.muted,
};
export const chartTooltip = {
  backgroundColor: palette.ink,
  borderWidth: 0,
  textStyle: { color: "#fbfbf8", fontSize: 11, fontFamily: "DM Sans" },
  extraCssText: "box-shadow:none;border-radius:4px",
} as const;

export type ChartDetail = { label: string; values: (string | number)[] };
export function AnalyticalChart({
  option,
  label,
  question,
  detail,
  className,
  onSelect,
}: {
  option: EChartsOption;
  label: string;
  question: string;
  detail: ChartDetail[];
  className?: string;
  onSelect?: (name: string) => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [table, setTable] = useState(
    () => window.matchMedia("(max-width: 900px)").matches,
  );
  const id = useId();
  useEffect(() => {
    const element = ref.current;
    if (!element) return;
    if (element.offsetWidth === 0 || element.offsetHeight === 0) return;
    const reduced = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;
    echarts.getInstanceByDom(element)?.dispose();
    const chart = echarts.init(element, undefined, { renderer: "canvas" });
    chart.setOption({ ...option, animation: !reduced });
    if (onSelect)
      chart.on("click", (event) => onSelect(String(event.name ?? "")));
    const observer = new ResizeObserver(() => chart.resize());
    observer.observe(element);
    return () => {
      observer.disconnect();
      chart.dispose();
    };
  }, [option, onSelect]);
  return (
    <div className="chart-frame">
      <div
        ref={ref}
        className={`echart ${className ?? ""}`}
        role="img"
        aria-label={label}
        aria-describedby={id}
      />
      <div className="chart-access">
        <p id={id}>
          <strong>{question}</strong>
        </p>
        <button
          type="button"
          onClick={() => setTable((v) => !v)}
          aria-expanded={table}
        >
          {table ? "Hide" : "Show"} exact chart values
        </button>
      </div>
      {table && (
        <div className="chart-detail-table" role="table">
          {detail.map((row) => (
            <div role="row" key={row.label}>
              <strong role="rowheader">{row.label}</strong>
              {row.values.map((value, index) => (
                <span role="cell" key={index}>
                  {value}
                </span>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function axisLabel(formatter?: (value: number) => string) {
  return { color: palette.muted, fontSize: 10, formatter };
}
export function valueGrid() {
  return { left: 48, right: 24, top: 36, bottom: 38, containLabel: true };
}
